/**
 * Freshness guard for the homepage live poll.
 *
 * /api/fetchData is CDN-cached and, right after a 5-minute data cycle boundary,
 * can briefly return the *previous* cycle while a fresher value is already in
 * the server-rendered page (or a later poll). Without a guard the client would
 * overwrite fresher data with that stale cached response, producing the visible
 * "jumps to fresh, then reverts to stale" flicker. These helpers let the client
 * ignore any poll that would move the timestamp backwards.
 */

import { parseISO } from 'date-fns';

// Parse an ISO timestamp to epoch ms. Missing / 'error' / unparseable → 0,
// so any real timestamp always compares as newer than "nothing".
export const parseTs = (value) => {
    if (!value || value === 'error') return 0;
    const ms = parseISO(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
};

// Should a freshly-polled payload (nextLastUpdated) replace the current one
// (prevLastUpdated)? Accept same-or-newer; reject older or timestamp-less.
export const shouldAcceptUpdate = (prevLastUpdated, nextLastUpdated) => {
    const next = parseTs(nextLastUpdated);
    if (!next) return false; // never accept a payload with no usable timestamp
    const prev = parseTs(prevLastUpdated);
    if (!prev) return true; // nothing valid yet → accept
    return next >= prev; // reject anything that moves backwards in time
};

// Whether a state object holds a usable headline wait value. Used so a failed
// poll keeps the last good value on screen instead of blanking it with an error.
export const hasUsableData = (state) =>
    !!(state && state.waitTime && state.waitTime !== 'error');
